// PharOS Beacon card — two jobs:
//   1. Focus popover: admins set/clear the page's steering text; the card
//      swaps to the loading shimmer and the regenerated card arrives over
//      the existing page_insights Turbo Stream broadcast.
//   2. Insight click-through: clicking a row opens the in-page PharOS panel
//      pre-seeded with a drill-down question (via a window event, so the
//      two controllers stay decoupled).
// Attached to the card CONTAINER (#insights-card-<id>) so it survives
// innerHTML swaps from broadcasts; all targets are guarded because the
// loading state has none of them.
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["popover", "focusInput"]
  static values = { focusUrl: String }

  connect() {
    this._outside = (e) => {
      if (!this.hasPopoverTarget || this.popoverTarget.hidden) return
      if (!e.target.closest(".laz-insights-focus-wrap")) this._hidePopover()
    }
    document.addEventListener("click", this._outside)
  }

  disconnect() {
    document.removeEventListener("click", this._outside)
  }

  toggleFocus(event) {
    event.stopPropagation()
    if (!this.hasPopoverTarget) return
    this.popoverTarget.hidden ? this._showPopover() : this._hidePopover()
  }

  saveFocus() {
    this._submit(this.hasFocusInputTarget ? this.focusInputTarget.value : "")
  }

  clearFocus() {
    this._submit("")
  }

  // Insight row → open the page panel with a pre-seeded question. Drives the
  // panel controller DIRECTLY via the Stimulus registry (deterministic);
  // falls back to the pharos:ask event for any other listener. No-op when the
  // panel isn't on this page or an inner control (button/link) was clicked.
  ask(event) {
    // Ignore clicks on interactive elements INSIDE the row only — closest()
    // walks to the document root, so an ancestor <a>/<button> outside the row
    // must not swallow the click.
    const inner = event.target.closest("button, a, textarea")
    if (inner && event.currentTarget.contains(inner)) return
    const question = event.currentTarget.dataset.beaconQuestion
    if (!question) return
    const panelEl = document.querySelector('[data-controller~="pharos-page-panel"]')
    if (!panelEl) return
    const panel = window.Stimulus?.getControllerForElementAndIdentifier(panelEl, "pharos-page-panel")
    if (panel) {
      panel.open()
      const input = panelEl.querySelector("#chat-input")
      if (input) {
        input.value = question
        input.focus()
        input.dispatchEvent(new Event("input", { bubbles: true }))
      }
    } else {
      window.dispatchEvent(new CustomEvent("pharos:ask", { detail: { question } }))
    }
  }

  _showPopover() {
    this.popoverTarget.hidden = false
    const chip = this.element.querySelector(".laz-insights-focus-chip")
    if (chip) chip.setAttribute("aria-expanded", "true")
    if (this.hasFocusInputTarget) this.focusInputTarget.focus()
  }

  _hidePopover() {
    this.popoverTarget.hidden = true
    const chip = this.element.querySelector(".laz-insights-focus-chip")
    if (chip) chip.setAttribute("aria-expanded", "false")
  }

  async _submit(focus) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const resp = await fetch(this.focusUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": token,
          "Content-Type": "application/x-www-form-urlencoded",
          "Accept": "text/html",
        },
        body: new URLSearchParams({ focus: focus || "" }),
        credentials: "same-origin",
      })
      if (resp.ok) {
        // Loading shimmer now; the Turbo Stream broadcast (subscribed outside
        // this container) swaps in the regenerated card when the job finishes.
        this.element.innerHTML = await resp.text()
      }
    } catch (_) {
      // Network hiccup — leave the current card in place rather than blanking it.
    }
  }
}
